import { useCallback } from "react";

import { cn } from "@/lib/utils";

import type { TableWidget, Widget, WidgetSlot, WorkshopPage } from "./types";
import {
  BarChartCard,
  HtmlCard,
  LineChartCard,
  ListCard,
  MarkdownCard,
  MetricCard,
  TableCard,
} from "./widgets";

const SPAN_CLASS: Record<WidgetSlot["span"], string> = {
  1: "col-span-6 sm:col-span-3 lg:col-span-1",
  2: "col-span-6 sm:col-span-3 lg:col-span-2",
  3: "col-span-6 sm:col-span-3",
  4: "col-span-6 lg:col-span-4",
  5: "col-span-6 lg:col-span-5",
  6: "col-span-6",
};

interface PageRendererProps {
  page: WorkshopPage;
  /**
   * Mutate a single widget in-place. The renderer hands this down to
   * editable widgets (today: ``TableCard``); other widget types will
   * gain edit affordances later.
   */
  onWidgetChange: (slotId: string, next: Widget) => void;
}

export function PageRenderer({ page, onWidgetChange }: PageRendererProps) {
  const renderSlot = useCallback(
    (slot: WidgetSlot) => {
      switch (slot.widget.type) {
        case "metric":
          return <MetricCard widget={slot.widget} />;
        case "bar-chart":
          return <BarChartCard widget={slot.widget} />;
        case "line-chart":
          return <LineChartCard widget={slot.widget} />;
        case "table":
          return (
            <TableCard
              widget={slot.widget}
              onChange={(next: TableWidget) => onWidgetChange(slot.id, next)}
            />
          );
        case "list":
          return <ListCard widget={slot.widget} />;
        case "markdown":
          return <MarkdownCard widget={slot.widget} />;
        case "html":
          return <HtmlCard widget={slot.widget} />;
        default: {
          // Exhaustiveness check — any new widget type without a render
          // arm will trip a compile error here.
          const _exhaustive: never = slot.widget;
          return null;
        }
      }
    },
    [onWidgetChange],
  );

  return (
    <div className="relative overflow-hidden rounded-2xl border border-border/50 bg-gradient-to-br from-panel via-card to-muted/30 p-4 shadow-md sm:p-6">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.55]"
        style={{
          backgroundImage: [
            "radial-gradient(820px 420px at 12% -8%, hsl(var(--primary) / 0.14), transparent 58%)",
            "radial-gradient(640px 360px at 92% 108%, hsl(var(--lemon) / 0.12), transparent 55%)",
          ].join(","),
        }}
      />
      <div className="relative grid grid-cols-6 gap-4">
        {page.widgets.map((slot, idx) => (
          <div
            key={slot.id}
            className={cn(SPAN_CLASS[slot.span], "animate-fade-in-up")}
            style={{
              animationDelay: `${Math.min(idx, 14) * 40}ms`,
              animationFillMode: "backwards",
            }}
          >
            {renderSlot(slot)}
          </div>
        ))}
      </div>
    </div>
  );
}
